import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import Modal from "@/components/controls/Modal";
import SingleSelect, {
  SingleSelectOption
} from "@/components/controls/SingleSelect";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { ContractMapping } from "@/entities/stripeCheckout/types";
import { FieldValuesEditor } from "@/pages/settings/components/integrations/stripeCheckout/FieldValuesEditor";
import {
  DURATION_UNITS,
  DurationUnit,
  MAX_PLANS,
  MAX_PLAN_LENGTH,
  PriceRow,
  STRIPE_CHECKOUT_PREFIX,
  mappingWithPrices,
  priceErrors,
  priceRows
} from "@/pages/settings/components/integrations/stripeCheckout/stripeCheckoutOptions";

const PREFIX = `${STRIPE_CHECKOUT_PREFIX}.prices`;

const SELECT_CLASSNAME = "text-start border border-border-border2 pl-0";
const SELECT_TRIGGER_CLASSNAME = "w-full min-h-8 h-9";

type KeyedRow = PriceRow & { key: number; isOpen: boolean };

interface PricesDialogProps {
  mapping: ContractMapping;
  serviceItemOptions: SingleSelectOption[];
  isCatalogLoading: boolean;
  isSaving: boolean;
  saveError: string | null;
  onSave: (mapping: ContractMapping) => void;
  onClose: () => void;
}

// Mounted per open, so the draft always starts from the saved mapping.
export const PricesDialog: React.FC<PricesDialogProps> = ({
  mapping,
  serviceItemOptions,
  isCatalogLoading,
  isSaving,
  saveError,
  onSave,
  onClose
}) => {
  const { t } = useTranslation();
  // Each row keeps its key when another is removed, so its open state stays.
  const nextKey = useRef(0);
  const keyed = (row: PriceRow): KeyedRow => ({
    ...row,
    key: nextKey.current++,
    isOpen: false
  });
  const [rows, setRows] = useState<KeyedRow[]>(() =>
    priceRows(mapping).map(keyed)
  );
  const errors = priceErrors(rows);

  const unitOptions: SingleSelectOption[] = DURATION_UNITS.map((unit) => ({
    id: unit,
    displayName: t(`${PREFIX}.units.${unit}`)
  }));

  const updateRow = (index: number, changes: Partial<KeyedRow>) =>
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, ...changes } : row))
    );

  return (
    <Modal
      open
      setIsOpen={(open) => {
        if (!open && !isSaving) onClose();
      }}
      contentClassname="w-[52rem]"
      contentContainerClassname="max-h-[70vh] overflow-y-auto"
      data-test="stripe-checkout-prices-dialog"
      title={
        <Text as="span" variant="lgSubtitleH1">
          {t(`${PREFIX}.dialogTitle`)}
        </Text>
      }
      content={
        <div className="flex flex-col gap-3 px-6 py-4">
          <Text variant="bodySecondary">{t(`${PREFIX}.dialogSubtitle`)}</Text>
          {rows.map((row, index) => (
            <div
              key={row.key}
              className="flex flex-col gap-2 border-b border-border-border1 pb-3"
              data-test={`stripe-checkout-price-${index}`}
            >
              <div className="flex items-center gap-2">
                <IconButton
                  variant="iconNoBg"
                  size="lg"
                  aria-expanded={row.isOpen}
                  aria-label={t(`${PREFIX}.a11y.toggleFields`, {
                    position: index + 1
                  })}
                  data-test={`stripe-checkout-price-fields-toggle-${index}`}
                  onClick={() => updateRow(index, { isOpen: !row.isOpen })}
                >
                  {row.isOpen ? <ChevronDown /> : <ChevronRight />}
                </IconButton>
                <Input
                  data-test={`stripe-checkout-price-plan-${index}`}
                  aria-label={t(`${PREFIX}.a11y.plan`, {
                    position: index + 1
                  })}
                  placeholder={t(`${PREFIX}.planPlaceholder`)}
                  value={row.plan}
                  onChange={(event) =>
                    updateRow(index, { plan: event.target.value })
                  }
                  maxLength={MAX_PLAN_LENGTH}
                  disabled={isSaving}
                  containerClassName="flex-1"
                />
                <div className="w-[16rem]">
                  <SingleSelect
                    fieldName={`serviceItem-${index}`}
                    options={serviceItemOptions}
                    selectedValue={row.serviceItemId ?? undefined}
                    onSelection={(value) =>
                      updateRow(index, { serviceItemId: String(value) })
                    }
                    formatter={(value) =>
                      serviceItemOptions.find((option) => option.id === value)
                        ?.displayName ?? String(value)
                    }
                    placeholder={t(`${PREFIX}.serviceItemPlaceholder`)}
                    showSearch
                    disabled={isSaving}
                    isLoading={isCatalogLoading}
                    className={SELECT_CLASSNAME}
                    popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                    variant="form"
                  />
                </div>
                <Input
                  data-test={`stripe-checkout-price-duration-${index}`}
                  aria-label={t(`${PREFIX}.a11y.durationCount`, {
                    position: index + 1
                  })}
                  placeholder={t(`${PREFIX}.durationPlaceholder`)}
                  inputMode="numeric"
                  value={row.durationCount}
                  onChange={(event) =>
                    updateRow(index, { durationCount: event.target.value })
                  }
                  disabled={isSaving}
                  containerClassName="w-[4.5rem]"
                />
                <div className="w-[7rem]">
                  <SingleSelect
                    fieldName={`durationUnit-${index}`}
                    options={unitOptions}
                    selectedValue={row.durationUnit}
                    onSelection={(value) =>
                      updateRow(index, {
                        durationUnit: String(value) as DurationUnit
                      })
                    }
                    formatter={(value) =>
                      unitOptions.find((option) => option.id === value)
                        ?.displayName ?? String(value)
                    }
                    disabled={isSaving}
                    className={SELECT_CLASSNAME}
                    popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                    variant="form"
                  />
                </div>
                <IconButton
                  variant="iconNoBg"
                  size="lg"
                  data-test={`stripe-checkout-price-remove-${index}`}
                  aria-label={t(`${PREFIX}.a11y.remove`, {
                    position: index + 1
                  })}
                  onClick={() =>
                    setRows((current) => current.filter((_, i) => i !== index))
                  }
                  disabled={isSaving}
                >
                  <Trash2 />
                </IconButton>
              </div>
              {row.isOpen && (
                <div className="flex flex-col gap-1 pl-10">
                  <Text variant="bodySecondary">
                    {t(`${PREFIX}.fieldsHint`)}
                  </Text>
                  <FieldValuesEditor
                    rows={row.fields}
                    onChange={(fields) => updateRow(index, { fields })}
                    disabled={isSaving}
                    testId={`stripe-checkout-price-fields-${index}`}
                  />
                </div>
              )}
            </div>
          ))}
          <Button
            variant="tertiary"
            className="w-fit"
            data-test="stripe-checkout-price-add"
            onClick={() =>
              setRows((current) => [
                ...current,
                keyed({
                  plan: "",
                  serviceItemId: null,
                  durationCount: "1",
                  durationUnit: "M",
                  fields: []
                })
              ])
            }
            disabled={isSaving || rows.length >= MAX_PLANS}
          >
            {t(`${PREFIX}.add`)}
          </Button>
          {(errors.length > 0 || saveError) && (
            <div
              className="flex flex-col gap-1"
              role="alert"
              data-test="stripe-checkout-prices-errors"
            >
              {errors.map((error) => (
                <Text key={error} variant="smBodyError">
                  {t(`${PREFIX}.errors.${error}`)}
                </Text>
              ))}
              {saveError && <Text variant="smBodyError">{saveError}</Text>}
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            data-test="stripe-checkout-prices-save"
            onClick={() => onSave(mappingWithPrices(mapping, rows))}
            disabled={isSaving || errors.length > 0}
          >
            {t("save")}
          </Button>
        </div>
      }
    />
  );
};
